/**
 * We all hate prop drilling so here is a tiny little context hook to handle nav
 */
import React from 'react';

import { useImmerReducer } from 'use-immer';

import { assertUnreachable } from '../../../../../util/assertUnreachable';

const FIRST_COPY_TO_WORKSPACE_STEP = 0;
const LAST_COPY_TO_WORKSPACE_STEP = 2;

type NextStep = { type: 'NextStep' }; // to go to quiz selection
type PreviousStep = { type: 'PreviousStep' }; // to go back to workspace selection

type SetWorkspace = {
  type: 'SetWorkspace';
  payload: { id: string; name: string };
};
type SetParentId = {
  type: 'SetParentId';
  payload: { newValue: string | null };
};

type CopyToWorkspaceAction =
  | NextStep
  | PreviousStep
  | SetWorkspace
  | SetParentId;

const initialCopyToWorkspaceState = {
  currentStep: FIRST_COPY_TO_WORKSPACE_STEP,
  workspaceId: null,
  workspaceName: null,
  parentId: null,
};

type CopyToWorkspaceState = {
  currentStep: number;
  workspaceId: string;
  workspaceName: string;
  parentId: string | null;
};

export type DispatchCopyToWorkspaceAction =
  React.Dispatch<CopyToWorkspaceAction>;

// reducer
const copyToWorkspaceReducer = (
  state: CopyToWorkspaceState,
  action: CopyToWorkspaceAction
) => {
  switch (action.type) {
    case 'NextStep': {
      const newStep = Math.min(
        state.currentStep + 1,
        LAST_COPY_TO_WORKSPACE_STEP
      );
      state.currentStep = newStep;
      break;
    }
    case 'PreviousStep': {
      const newStep = Math.max(
        state.currentStep - 1,
        FIRST_COPY_TO_WORKSPACE_STEP
      );
      if (newStep === FIRST_COPY_TO_WORKSPACE_STEP) {
        // Step 0 is selecting workspaces, therefore these should all be reset
        state.workspaceName = null;
        state.workspaceId = null;
      }
      state.currentStep = newStep;
      break;
    }
    case 'SetWorkspace': {
      const { id, name } = action.payload;
      state.workspaceId = id;
      state.workspaceName = name;
      break;
    }
    case 'SetParentId': {
      const { newValue } = action.payload;
      state.parentId = newValue;
      break;
    }
    default: {
      assertUnreachable(action);
    }
  }
};

// context
const CopyToWorkspaceContext = React.createContext<{
  copyToWorkspaceState: CopyToWorkspaceState;
  dispatchCopyToWorkspaceAction: DispatchCopyToWorkspaceAction;
} | null>(null);

// providah
export const CopyToWorkspaceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useImmerReducer(
    copyToWorkspaceReducer,
    initialCopyToWorkspaceState
  );

  return (
    <CopyToWorkspaceContext.Provider
      value={{
        copyToWorkspaceState: state,
        dispatchCopyToWorkspaceAction: dispatch,
      }}
    >
      {children}
    </CopyToWorkspaceContext.Provider>
  );
};

// custom hook to interact with the context
export const useCopyDialog = () => {
  const {
    copyToWorkspaceState: state,
    dispatchCopyToWorkspaceAction: dispatch,
  } = React.useContext(CopyToWorkspaceContext);

  // function to move forward
  const nextStep = React.useCallback(() => {
    dispatch({ type: 'NextStep' });
  }, [dispatch]);

  // function to go back
  const prevStep = React.useCallback(() => {
    dispatch({ type: 'PreviousStep' });
  }, [dispatch]);

  // function to set the workspaceId
  const setWorkspace = React.useCallback(
    ({ name, id }: { name: string; id: string }) => {
      dispatch({ type: 'SetWorkspace', payload: { name, id } });
    },
    [dispatch]
  );

  // function to set the parentId
  const setParentId = (id: string | null) => {
    dispatch({ type: 'SetParentId', payload: { newValue: id } });
  };

  // return those fns
  return { nextStep, prevStep, setWorkspace, setParentId, ...state };
};
